// ---------------------------------------------------------------------------
// "Re-interpret ICP": the Plain English ICP wins and overwrites
//
// The detail filter fields (HQ location, industry, employee count, …) are an
// INTERPRETATION of the Plain English ICP. The ordinary preview refresh keeps a
// hand-edit to one of them (that is a feature: you can nudge a filter without
// re-describing the whole ICP). The explicit "Re-interpret ICP" is the opposite
// intent — regenerate the detail fields from the ICP text FROM SCRATCH — and it
// was not honouring it: a hand-added "USA" on an ICP that says "Europe" survived
// the reinterpret, in the UI and in the search (UAT, "Plain English ICP Wins and
// Overwrites" — Not Passed).
//
// The fix is to clear every ICP-controlled field before the fresh interpretation
// runs. With them empty, the interpretation drives BOTH halves wholesale: the
// Apollo request (empty fields → the server maps purely from the text, nothing
// stale unioned in) and the visible form (the apply-effect fills a field only
// when it is empty or still exactly what a prior interpretation wrote). The ICP
// text itself and the lead limit are the INPUT and the COUNT, not interpreted
// output, so they are deliberately left untouched.
// ---------------------------------------------------------------------------

/**
 * The Apollo filter fields an ICP interpretation owns — everything a fresh
 * "Re-interpret ICP" regenerates from the Plain English text. Deliberately NOT
 * `icpDescription` (the text being interpreted) or `leadLimit` (the count).
 */
export interface IcpControlledFields {
  companyLocation: string[];
  companyIndustry: string[];
  industryKeywords: string;
  employeeCount: string[];
  revenueMin: number | null;
  revenueMax: number | null;
  jobTitle: string;
  department: string[];
  personLocation: string[];
}

/**
 * Return `params` with every ICP-controlled field reset to empty and all other
 * fields (icpDescription, leadLimit, …) left exactly as they were. Pure — takes
 * no view of WHY a field holds what it holds; the caller decides when a wholesale
 * reset is what the user asked for (only the explicit reinterpret, never the
 * ordinary refresh).
 */
export function clearIcpControlledFields<T extends IcpControlledFields>(params: T): T {
  return {
    ...params,
    companyLocation: [],
    companyIndustry: [],
    industryKeywords: '',
    employeeCount: [],
    revenueMin: null,
    revenueMax: null,
    jobTitle: '',
    department: [],
    personLocation: [],
  };
}
